import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';

import Table from 'Components/Table';
import { Text, Box, Flex, Icon, ICON_LIST } from 'Components/Base';
import { SYLLABUS_MODE } from 'Domains/syllabus';
import { MASTERY_LEVEL } from 'Domains/mastery';
import Tooltip from 'Components/Tooltip';

const TextWrapper = styled(Box)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
  font-weight: ${({ fontWeight }) => fontWeight ?? 'normal'};
`;

const AttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 5px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 5) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      &:nth-child(1) {
        min-width: 100px;
        width: 100px;
      }
    }
  }

  & .MuiTableRow-root.MuiTableRow-hover:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const TKAttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 12px 16px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      position: sticky;
      top: 0;
      z-index: 3;
      &:nth-child(n + 5) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 6) {
        border-bottom: none;
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      position: sticky;
      top: 55.5px !important;
      z-index: 3;
      &:nth-child(1) {
        min-width: 100px;
        width: 100px;
      }

      &:nth-child(-n + 4) {
        > div {
          position: relative;
          top: -25px;
        }
        &:nth-child(4) {
          > div {
            display: flex;
            justify-content: center;
          }
        }
      }

      &:nth-child(n + 7) {
        > div {
          position: relative;
          top: -25px;
        }
      }
    }
  }

  & .MuiTableRow-root.MuiTableRow-hover:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const TableHead = props => <Text fontWeight={600} {...props} />;
const HeaderColSpanText = styled(TableHead)`
  white-space: nowrap;
`;

export const CourseUnitAttendanceTable = ({
  data: { reports = [], courseName },
  maxHeight,
  mode,
  ...props
}) => {
  const { t } = useTranslation('courseUnit');
  const history = useHistory();
  const queryString = new URLSearchParams(window.location.search);
  const theme = useContext(ThemeContext);

  const getTextColor = value => {
    if (value > 0) return theme.colors.passText;
    if (value < 0) return theme.colors.failText;
  };

  const columns = [
    {
      id: 'classNo',
      Header: <TableHead>{t('CLASS')}</TableHead>,
      accessor: properties => properties.schoolGrade.shortName + '/' + properties.classNo,
      width: 100,
      Cell: ({ cell: { row } }) => (
        <TextWrapper minWidth={'100px'}>
          {row?.original?.schoolGrade.shortName}/{row?.original?.classNo}
        </TextWrapper>
      ),
    },
    {
      id: 'studentNo',
      Header: <TableHead>{t('NO')}</TableHead>,
      accessor: 'studentNo',
      width: 100,
      Cell: ({ cell: { value } }) => <TextWrapper>{value}</TextWrapper>,
    },
    {
      id: 'username',
      Header: <TableHead>{t('USERNAME')}</TableHead>,
      accessor: 'classroomUsername',
      width: 140,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'140px'}>{value}</TextWrapper>,
    },
    {
      id: 'displayName',
      Header: <TableHead>{t('DISPLAY_NAME')}</TableHead>,
      accessor: properties => properties.firstName + ' ' + properties.lastName,
      Cell: ({ cell: { row } }) => (
        <TextWrapper style={{ textAlign: 'left' }}>
          {row?.original?.firstName} {row?.original?.lastName}
        </TextWrapper>
      ),
    },
    {
      id: 'courseUnit',
      Header: <HeaderColSpanText>{t('UNIT', { unitName: courseName ?? '' })}</HeaderColSpanText>,
      columns: [
        {
          id: 'attendanceRate',
          Header: <TableHead>{t('ATTENDANCE_RATE')}</TableHead>,
          accessor: 'attendanceRate',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <TextWrapper>{`${value.actual}/${value.total}`}</TextWrapper>
          ),
        },
        {
          id: 'examinationRate',
          Header: <TableHead>{t('EXAMINATION_RATE')}</TableHead>,
          accessor: 'examinationRate',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <TextWrapper>{`${value.actual}/${value.total}`}</TextWrapper>
          ),
        },
        {
          id: 'growthRate',
          Header: <TableHead>{t('GROWTH_RATE')}</TableHead>,
          accessor: 'growthRate',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => {
            if (value !== null) {
              const formatValue = `${_.round(value)}%`;
              return (
                <TextWrapper color={getTextColor(value)} fontWeight={'bold'}>
                  {value > 0 ? `+${formatValue}` : formatValue}
                </TextWrapper>
              );
            } else {
              return null;
            }
          },
        },
      ],
    },
  ];

  const tkColumns = [
    {
      id: 'classNo',
      Header: <TableHead>{t('CLASS')}</TableHead>,
      accessor: properties => properties.schoolGrade.shortName + '/' + properties.classNo,
      width: 100,
      Cell: ({ cell: { row } }) => (
        <TextWrapper minWidth={'100px'}>
          {row?.original?.schoolGrade.shortName}/{row?.original?.classNo}
        </TextWrapper>
      ),
    },
    {
      id: 'studentNo',
      Header: <TableHead>{t('NO')}</TableHead>,
      accessor: 'studentNo',
      width: 100,
      Cell: ({ cell: { value } }) => <TextWrapper>{value}</TextWrapper>,
    },
    {
      id: 'username',
      Header: <TableHead>{t('USERNAME')}</TableHead>,
      accessor: 'classroomUsername',
      width: 140,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'140px'}>{value}</TextWrapper>,
    },
    {
      id: 'displayName',
      Header: <TableHead>{t('DISPLAY_NAME')}</TableHead>,
      accessor: properties => properties.firstName + ' ' + properties.lastName,
      width: 250,
      Cell: ({ cell: { row } }) => (
        <TextWrapper style={{ textAlign: 'left' }}>
          {row?.original?.firstName} {row?.original?.lastName}
        </TextWrapper>
      ),
    },
    {
      id: 'mission',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <HeaderColSpanText>{t('MISSION_COMPLETE')}</HeaderColSpanText>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  {t('MISSION_COMPLETE')}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    marginTop: '8px',
                  }}
                >
                  {t('VIDEO_DESCRIPTION')}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {t('QUIZ_DESCRIPTION')}
                </Text>
              </>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      columns: [
        {
          id: 'attendanceRate',
          Header: <TableHead>{t('VIDEO')}</TableHead>,
          accessor: 'attendanceRate',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <TextWrapper>{`${value.actual}/${value.total}`}</TextWrapper>
          ),
        },
        {
          id: 'examinationRate',
          Header: <TableHead>{t('QUIZ')}</TableHead>,
          accessor: 'examinationRate',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <TextWrapper>{`${value.actual}/${value.total}`}</TextWrapper>
          ),
        },
      ],
    },
    {
      id: 'growthRate',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('GROWTH_RATE')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  {t('GROWTH_RATE')}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    marginTop: '8px',
                  }}
                >
                  {t('GROWTH_RATE_TOOLTIP')}
                </Text>
              </>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'growthRate',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => {
        if (value !== null) {
          const formatValue = `${_.round(value)}%`;
          return (
            <TextWrapper color={getTextColor(value)}>
              {value > 0 ? `+${formatValue}` : formatValue}
            </TextWrapper>
          );
        } else {
          return null;
        }
      },
    },
    {
      id: 'mastery',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('MASTERY')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                  }}
                >
                  {t('DESCRIPTION_MASTERY')}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    marginTop: '8px',
                  }}
                >
                  {t('DEFINITION_MASTERY')}
                </Text>
              </>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'mastery',
      disableSortBy: true,
      width: 200,
      Cell: ({ cell: { value } }) => {
        const masteryResult = MASTERY_LEVEL[value];
        return (
          <Flex style={{ alignItems: 'center', justifyContent: 'center' }}>
            <Box
              style={{
                backgroundColor: masteryResult.color,
                width: '14px',
                height: '14px',
                flexShrink: 0,
                borderRadius: '50%',
              }}
            />
            <TextWrapper style={{ marginLeft: '8px' }}>{masteryResult.name}</TextWrapper>
          </Flex>
        );
      },
    },
    {
      id: 'bloom',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('BLOOM')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  {t('BLOOM')}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    marginTop: '8px',
                  }}
                >
                  {t('BLOOM_TOOLTIP')}
                </Text>
              </>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'bloom',
      disableSortBy: true,
      width: 200,
      Cell: ({ cell: { value } }) => {
        return <TextWrapper>{value}</TextWrapper>;
      },
    },
  ];

  const onRowClick = rowProps => {
    const username = rowProps?.original?.classroomUsername;
    const classNo = rowProps?.original?.classNo;
    const schoolGradeId = rowProps?.original?.schoolGrade.id;
    const term = queryString.get('term');
    const subject = queryString.get('subj');
    const syllabus = queryString.get('course');
    const classFilter = queryString.get('classFilter') ?? queryString.get('class') ?? null;
    const gradeFilter = queryString.get('gradeFilter') ?? queryString.get('grade') ?? null;
    const mode = queryString.get('mode');
    const destinationPath = `${window.location.pathname}/${username}?term=${term}&grade=${schoolGradeId}&class=${classNo}&subj=${subject}&course=${syllabus}&classFilter=${classFilter}&gradeFilter=${gradeFilter}&mode=${mode}`;
    history.push(destinationPath);
  };

  return (
    <>
      {mode === SYLLABUS_MODE.TRADITIONAL ? (
        <AttendanceTableStyle
          dense
          stickyHeader
          columns={columns}
          data={reports}
          style={{ maxHeight }}
          getRowProps={rowProps => ({
            style: { cursor: 'pointer' },
            hover: true,
            onClick: () => {
              onRowClick(rowProps);
            },
          })}
          {...props}
        />
      ) : (
        <TKAttendanceTableStyle
          dense
          stickyHeader
          columns={tkColumns}
          data={reports}
          style={{ maxHeight }}
          getRowProps={rowProps => ({
            style: { cursor: 'pointer' },
            hover: true,
            onClick: () => {
              onRowClick(rowProps);
            },
          })}
          {...props}
        />
      )}
    </>
  );
};

CourseUnitAttendanceTable.propTypes = {
  data: PropTypes.shape({ reports: PropTypes.array.isRequired, courseName: PropTypes.string }),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mode: PropTypes.string,
};

export default CourseUnitAttendanceTable;
